import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  TablePagination,
  TableSortLabel,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import moment from "moment-timezone";

import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import { tokens } from "../../theme";
import { mono, pill, statusKind, th, td, filterFieldSx, FactRows, TablePaginationActions } from "../../components/tableKit";

const rtypeLabel = (rtype) =>
  rtype == 24 ? "Corepay" : rtype == 2 ? "Stripe" : rtype == 1 ? "Skrill" : rtype == 3 ? "Now Payments" : "";

const fmtDate = (d) => (d && moment.utc(d).isValid() ? moment.utc(d).format("YYYY-MM-DD") : "");

// sagacoins comes back as a float (80.0); drop the pointless trailing zeros
// but keep real fractions.
const fmtCoins = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const Payments = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [playerList, setPlayerList] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [playerTypeFilter, setPlayerTypeFilter] = useState("");

  const [viewShow, setViewShow] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    paymentsData();
    getPlayerTypes();
  }, []);

  const getPlayerTypes = async () => {
    try {
      const response = await getAllData(`${endPoints.api.playertypes}`);
      if (response?.status === "success") {
        setPlayerList(response.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Shared by the list fetch and the CSV export so both hit the API with
  // the same fromDate/toDate/userId/playerType/status params. `overrides`
  // lets callers (e.g. Clear) use explicit values instead of waiting on
  // state updates to land.
  const buildFilterQuery = (overrides = {}) => {
    const filters = {
      fromDate,
      toDate,
      userId: userIdFilter,
      playerType: playerTypeFilter,
      status: statusFilter,
      ...overrides,
    };

    const params = new URLSearchParams();
    if (filters.fromDate) params.set("fromDate", filters.fromDate);
    if (filters.toDate) params.set("toDate", filters.toDate);
    if (filters.userId) params.set("userId", filters.userId);
    if (filters.playerType) params.set("playerType", filters.playerType);
    if (filters.status) params.set("status", filters.status);
    return params.toString();
  };

  const paymentsData = async (overrides = {}) => {
    const query = buildFilterQuery(overrides);
    let url = `${endPoints.api.GET_COIN_TRANSACTION}/${1}/${1000}${query ? `?${query}` : ""}`;
    setIsLoading(true);
    setPage(0);
    await getAllData(url)
      .then((response) => {
        setIsLoading(false);
        if (response?.status === "success") {
          setRows(response.data || []);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
      });
  };

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setUserIdFilter("");
    setPlayerTypeFilter("");
    setStatusFilter("");
    setSearchText("");
    paymentsData({ fromDate: "", toDate: "", userId: "", playerType: "", status: "" });
  };

  const downloadCsv = () => {
    const query = buildFilterQuery();
    const url = `${endPoints.apiBaseUrl}${endPoints.api.GET_COIN_TRANSACTION_CSV}${query ? `?${query}` : ""}`;
    const SessonId = sessionStorage.getItem("session")?.replace('"', "").replace('"', "");
    setIsLoading(true);
    fetch(url, { method: "GET", headers: { Accept: "text/csv", SessonId } })
      .then((response) => {
        setIsLoading(false);
        return response.blob();
      })
      .then((blob) => {
        const link = document.createElement("a");
        const blobUrl = URL.createObjectURL(blob);
        link.href = blobUrl;
        link.download = `transactions_${moment().format("YYYYMMDD_HHmmss")}.csv`;
        link.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
      });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  // Build the status options from what actually came back, so the dropdown
  // tracks the API instead of a hardcoded list.
  const statusOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((row) => {
      const raw = String(row?.status ?? "").trim();
      if (raw && !seen.has(raw.toLowerCase())) seen.set(raw.toLowerCase(), raw);
    });
    return [...seen.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [rows]);

  const rowsToDisplay = useMemo(() => {
    let result = rows;

    if (statusFilter) {
      result = result.filter(
        (row) =>
          String(row.status || "").toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter(
        (row) =>
          String(row.id_user || "").includes(q) ||
          String(row.login || "").toLowerCase().includes(q) ||
          String(`${row.name || ""} ${row.surname || ""}`).toLowerCase().includes(q) ||
          String(row.transaction_id || "").toLowerCase().includes(q) ||
          String(row.merchantRefNum || "").toLowerCase().includes(q) ||
          String(row.status || "").toLowerCase().includes(q) ||
          String(row.packagename || "").toLowerCase().includes(q) ||
          String(row.playerType || "").toLowerCase().includes(q) ||
          rtypeLabel(row.rtype).toLowerCase().includes(q)
      );
    }

    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let av = sortConfig.key === "rtype" ? rtypeLabel(a.rtype) : a[sortConfig.key];
        let bv = sortConfig.key === "rtype" ? rtypeLabel(b.rtype) : b[sortConfig.key];
        av = av ?? "";
        bv = bv ?? "";
        let cmp;
        if (!isNaN(Number(av)) && !isNaN(Number(bv)) && av !== "" && bv !== "") {
          cmp = Number(av) - Number(bv);
        } else {
          cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
        }
        return sortConfig.direction === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [rows, searchText, statusFilter, sortConfig]);

  const sortableTh = (key, label, align) => (
    <th style={{ ...th, textAlign: align === "right" ? "right" : "left" }}>
      <TableSortLabel
        active={sortConfig.key === key}
        direction={sortConfig.key === key ? sortConfig.direction : "asc"}
        onClick={() => handleSort(key)}
        sx={{ "&.MuiTableSortLabel-root": { color: "inherit" }, "& .MuiTableSortLabel-icon": { fontSize: 14 } }}
      >
        {label}
      </TableSortLabel>
    </th>
  );

  const detailFacts = (d) => [
    ["Customer ID", d?.id_user],
    ["Transaction ID", d?.transaction_id],
    ["Player Type", d?.playerType || "—"],
    ["Customer Name", `${d?.name || ""} ${d?.surname || ""}`.trim() || "—"],
    ["User Name", d?.login],
    ["Transaction Date", fmtDate(d?.addeddate) || "—"],
    ["Amount", d?.amount != null ? `$${d.amount}` : "—"],
    ["Settle Amount", d?.settleAmount != null ? `$${d.settleAmount}` : "—"],
    ["Transaction Type", rtypeLabel(d?.rtype) || "—"],
    ["Package", d?.packagename || "—"],
    ["Saga Coins", fmtCoins(d?.sagacoins) ?? "—"],
    ["Status", d?.status],
  ];

  return (
    <Box>
      <Toaster></Toaster>
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <PageHeader
        title="Payments"
        crumb="Finance / Payments"
        subtitle="Coin package purchases & payment gateway settlements"
      />

      <SectionCard>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by customer ID, name, username, transaction, package, player type or status"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 340, "& .MuiInputBase-root": { fontSize: 13 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={filterFieldSx}>
            <Select
              displayEmpty
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              input={<OutlinedInput />}
              sx={{ fontSize: 13 }}
              renderValue={(selected) =>
                selected === "" ? (
                  <span style={{ color: tokens.textDim }}>All statuses</span>
                ) : (
                  selected
                )
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={filterFieldSx}>
            <Select
              displayEmpty
              value={playerTypeFilter}
              onChange={(e) => setPlayerTypeFilter(e.target.value)}
              input={<OutlinedInput />}
              sx={{ fontSize: 13 }}
              renderValue={(selected) =>
                selected === "" ? (
                  <span style={{ color: tokens.textDim }}>All player types</span>
                ) : (
                  playerList.find((p) => p.id === Number(selected))?.player_type || selected
                )
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              <MenuItem value="">All player types</MenuItem>
              {playerList.map((player) => (
                <MenuItem key={player.id} value={player.id}>
                  {player.player_type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Customer ID"
            placeholder="Enter customer ID"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && paymentsData()}
            sx={filterFieldSx}
          />

          <TextField
            size="small"
            type="date"
            label="From"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...filterFieldSx, minWidth: 150 }}
          />

          <TextField
            size="small"
            type="date"
            label="To"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...filterFieldSx, minWidth: 150 }}
          />

          <Button variant="contained" size="small" onClick={() => paymentsData()} sx={{ textTransform: "none" }}>
            Search
          </Button>
          <Button variant="outlined" size="small" color="inherit" onClick={clearFilters} sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}>
            Clear
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={downloadCsv}
            sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border, "&:hover": { borderColor: tokens.border2 } }}
          >
            Export
          </Button>

          <Typography sx={{ fontSize: 12, color: tokens.textDim }}>
            {`${rowsToDisplay.length} of ${rows.length} transactions`}
          </Typography>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {sortableTh("id_user", "Player")}
                {sortableTh("playerType", "Player Type")}
                {sortableTh("merchantRefNum", "Paysafe ID")}
                {sortableTh("rtype", "Type")}
                {sortableTh("packagename", "Package")}
                {sortableTh("sagacoins", "SC", "right")}
                {sortableTh("addeddate", "Trans Date")}
                {sortableTh("amount", "Amount", "right")}
                {sortableTh("settleAmount", "Settle", "right")}
                {sortableTh("status", "Status")}
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {rowsToDisplay.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={11} style={{ ...td, textAlign: "center", color: tokens.textDim }}>
                    No coin transactions found
                  </td>
                </tr>
              )}
              {rowsToDisplay
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <tr key={row.id_skrill_wallet_transaction}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{row.login}</div>
                      <div style={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2 }}>
                        #{row.id_user} · {row.name} {row.surname}
                      </div>
                    </td>
                    <td style={td}>
                      {row.playerType ? (
                        pill(row.playerType, "New Player")
                      ) : (
                        <span style={{ color: tokens.textDim2 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...td, fontFamily: mono }}>{row.merchantRefNum}</td>
                    <td style={td}>{rtypeLabel(row.rtype) ? pill(rtypeLabel(row.rtype), "neutral") : ""}</td>
                    <td style={td}>{row.packagename || <span style={{ color: tokens.textDim2 }}>—</span>}</td>
                    <td style={{ ...td, fontFamily: mono, textAlign: "right", color: tokens.secondary }}>
                      {fmtCoins(row.sagacoins) ?? <span style={{ color: tokens.textDim2 }}>—</span>}
                    </td>
                    <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>{fmtDate(row.addeddate)}</td>
                    <td style={{ ...td, fontFamily: mono, fontWeight: 700, textAlign: "right" }}>${row.amount}</td>
                    <td style={{ ...td, fontFamily: mono, textAlign: "right" }}>${row.settleAmount}</td>
                    <td style={td}>{row.status ? pill(row.status, statusKind(row.status)) : ""}</td>
                    <td style={td}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 15 }} />}
                        sx={{ textTransform: "none", color: tokens.textDim, borderColor: tokens.border }}
                        onClick={() => {
                          setViewShow(true);
                          setEditData(row);
                        }}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Box>
        </Box>

        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={rowsToDisplay.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          ActionsComponent={TablePaginationActions}
          sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
        />
      </SectionCard>

      <Dialog
        open={viewShow}
        onClose={() => setViewShow(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { background: tokens.cardBg, border: `1px solid ${tokens.border}`, borderRadius: "12px" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, borderBottom: `1px solid ${tokens.border}` }}>
          <Typography variant="h6" sx={{ fontSize: 16 }}>Transaction Details</Typography>
          <IconButton size="small" onClick={() => setViewShow(false)} aria-label="close">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <FactRows facts={detailFacts(editData)} />
        </Box>
      </Dialog>
    </Box>
  );
};

export default Payments;
